import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Save, Trash2, Users } from 'lucide-react';
import { ProgramCandidate, ProgramCandidatesApi, ProgramItemCatalog } from '@/services/program-candidates.api';
import { AcademicClassesApi, AcademicYearsApi } from '@/services/academic.api';
import { StudentsApi } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Checkbox, Field, Select } from '@/components/ui/Input';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, useDataTable } from '@/components/data-table';
import { usePermissions } from '@/hooks/usePermissions';

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.response?.data?.message ?? anyE?.message ?? 'Something went wrong';
}

const TYPE_LABEL: Record<string, string> = { single: 'Single', group: 'Group', team: 'Team' };
const TYPE_TONE: Record<string, 'green' | 'blue' | 'slate'> = { single: 'green', group: 'blue', team: 'blue' };

// "Group Song" — explicit product direction, hardcoded to this one item
// (not a general group-item rule): students from Grade 3, 4, or 5 can all
// join the same team (normally an item is locked to the student's own
// class), and a team must be all-boys or all-girls, never mixed — the
// backend enforces the gender rule too (see program-candidates.service.ts),
// this is a pre-check so the picker doesn't waste a round trip.
const GROUP_SONG_ITEM_ID = '6d59a1ca-617e-4f16-aa7d-396fcda931f6';
const GROUP_SONG_GRADES = ['Grade 3', 'Grade 4', 'Grade 5'];

const columnHelper = createColumnHelper<ProgramCandidate>();

/**
 * Program Management, Part 1 — Candidate Registration. Business rules
 * (max 1 Quran item, max 2 single items, max 1 group/team item; a
 * single item can be claimed by only one student per school; group
 * items link every registrant to one leader) are enforced server-side —
 * this UI just surfaces the same caps so the picker doesn't waste a
 * round trip on an obviously-invalid selection. Judging/ranking/
 * scoreboards/certificates are a separate, later Program Dashboard task
 * — this page is registration only.
 */
export function ProgramCandidatesPage() {
  const { can } = usePermissions();
  const canWrite = can('/program-candidates', 'create');
  const canDelete = can('/program-candidates', 'delete');
  const qc = useQueryClient();

  const { data: catalog = [] } = useQuery({
    queryKey: ['program-items-catalog'],
    queryFn: () => ProgramCandidatesApi.catalog(),
  });

  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-program-candidates'],
    queryFn: () => AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
  });
  const years = yearsPage?.items ?? [];

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-program-candidates'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];

  const [academicYearId, setAcademicYearId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [studentId, setStudentId] = useState('');
  const [deleting, setDeleting] = useState<ProgramCandidate | null>(null);
  // Client-side pagination of the "All registrations" table — the
  // registration panel above needs the FULL candidate list in memory for
  // its quota checks (existingCandidate / totalQuota), so this can't be a
  // server-paginated fetch without duplicating that query; DataTable
  // doesn't care whether its PaginatedResult came from the server or was
  // sliced locally.
  const registrationsTable = useDataTable({ defaultLimit: 20 });
  useEffect(() => {
    registrationsTable.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicYearId]);

  useEffect(() => {
    if (years.length && !academicYearId) setAcademicYearId(years.find((y: any) => y.isCurrent)?.id ?? years[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years]);

  const selectedClass = classes.find((c) => c.id === classId);
  const sections = selectedClass?.sections ?? [];

  const { data: studentsPage } = useQuery({
    queryKey: ['students', 'program-candidates', classId, sectionId, academicYearId],
    queryFn: () => StudentsApi.list({ classId, sectionId: sectionId || undefined, academicYearId, limit: 100 }),
    enabled: !!classId && !!academicYearId,
  });
  const students = studentsPage?.items ?? [];

  // Scoped to the same Academic Year picked above — "which year am I
  // registering into" and "which year's registrations am I viewing" are
  // the same control on this page, so the existing selector drives both
  // rather than adding a second, redundant one right next to it.
  const { data: candidates = [] } = useQuery({
    queryKey: ['program-candidates', academicYearId],
    queryFn: () => ProgramCandidatesApi.list({ academicYearId: academicYearId || undefined }),
  });

  const existingCandidate = candidates.find((c) => c.studentId === studentId && c.academicYearId === academicYearId);

  const registrationsResult = useMemo(() => {
    const { page, limit } = registrationsTable;
    const start = (page - 1) * limit;
    return {
      items: candidates.slice(start, start + limit),
      total: candidates.length,
      page,
      limit,
      totalPages: Math.max(1, Math.ceil(candidates.length / limit)),
    };
  }, [candidates, registrationsTable.page, registrationsTable.limit]);

  const registrationColumns = useMemo(() => {
    const cols = [
      columnHelper.display({
        id: 'no',
        header: 'No.',
        enableSorting: false,
        cell: (ctx) => (
          <span className="text-slate-400">
            {(registrationsTable.page - 1) * registrationsTable.limit + ctx.row.index + 1}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'student',
        header: 'Student',
        cell: (ctx) => (
          <div>
            <div className="font-medium text-slate-900">{ctx.row.original.studentName}</div>
            <div className="text-xs text-slate-400">{ctx.row.original.admissionNo}</div>
          </div>
        ),
      }),
      columnHelper.display({
        id: 'classSection',
        header: 'Class · Section',
        cell: (ctx) => `${ctx.row.original.className} · ${ctx.row.original.sectionName}`,
      }),
      columnHelper.display({
        id: 'items',
        header: 'Items',
        cell: (ctx) => (
          <div className="flex flex-wrap gap-1">
            {ctx.row.original.items.map((i) => (
              <Badge key={i.id} tone={TYPE_TONE[i.programItemType] ?? 'slate'}>
                {i.programItemName}
              </Badge>
            ))}
          </div>
        ),
      }),
      columnHelper.accessor('chestNumber', {
        header: 'Chest #',
        cell: (ctx) => ctx.getValue() ?? '—',
      }),
    ];
    if (canDelete) {
      cols.push(
        columnHelper.display({
          id: 'action',
          header: '',
          cell: (ctx) => (
            <button
              type="button"
              title="Remove registration"
              className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
              onClick={() => setDeleting(ctx.row.original)}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          ),
        }),
      );
    }
    return cols;
  }, [canDelete, registrationsTable.page, registrationsTable.limit]);

  // Legacy scopes item choices by the student's own grade — best-effort match on class name.
  // "Group Song" is a deliberate exception — see its constant's comment above.
  const eligibleItems = useMemo(
    () =>
      catalog.filter((i) => {
        if (i.id === GROUP_SONG_ITEM_ID) return !selectedClass || GROUP_SONG_GRADES.includes(selectedClass.name);
        return !selectedClass || !i.gradeCategory || i.gradeCategory === selectedClass.name;
      }),
    [catalog, selectedClass],
  );

  const existingItemIds = new Set(existingCandidate?.items.map((i) => i.programItemId) ?? []);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  useEffect(() => setChecked({}), [studentId, academicYearId]);

  // "Group Song" gender pre-check — see the constant's comment above.
  // Finds the existing team's gender from ANY candidate (across all
  // classes, since the team can span Grade 3/4/5) already on this item.
  const selectedStudentGender = students.find((s) => s.id === studentId)?.gender ?? null;
  const groupSongTeamGender =
    candidates.find((c) => c.items.some((i) => i.programItemId === GROUP_SONG_ITEM_ID))?.gender ?? null;
  const groupSongGenderMismatch =
    !!selectedStudentGender && !!groupSongTeamGender && selectedStudentGender !== groupSongTeamGender && !existingItemIds.has(GROUP_SONG_ITEM_ID);

  const quotaFor = (items: (ProgramItemCatalog | ProgramCandidate['items'][number])[]) => {
    let quran = 0;
    let single = 0;
    let group = 0;
    for (const i of items) {
      const isQuran = 'quranProgram' in i ? i.quranProgram : catalog.find((c) => c.id === i.programItemId)?.quranProgram;
      const type = 'type' in i ? i.type : i.programItemType;
      if (isQuran) quran++;
      else if (type === 'single') single++;
      else group++;
    }
    return { quran, single, group };
  };

  const existingQuota = quotaFor(existingCandidate?.items ?? []);
  const checkedItems = eligibleItems.filter((i) => checked[i.id]);
  const checkedQuota = quotaFor(checkedItems);
  const totalQuota = { quran: existingQuota.quran + checkedQuota.quran, single: existingQuota.single + checkedQuota.single, group: existingQuota.group + checkedQuota.group };

  const isDisabled = (item: ProgramItemCatalog) => {
    if (existingItemIds.has(item.id) || checked[item.id]) return false;
    if (item.quranProgram) return totalQuota.quran >= 1;
    if (item.type === 'single') return totalQuota.single >= 2;
    return totalQuota.group >= 1;
  };

  const [registerError, setRegisterError] = useState<string | undefined>();
  const register = useMutation({
    mutationFn: () => {
      const programItemIds = checkedItems.map((i) => i.id);
      if (!programItemIds.length) throw new Error('Select at least one item to register');
      return ProgramCandidatesApi.register({ studentId, academicYearId, programItemIds });
    },
    onSuccess: () => {
      setChecked({});
      setRegisterError(undefined);
      qc.invalidateQueries({ queryKey: ['program-candidates'] });
    },
    onError: (e) => setRegisterError(errMsg(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => ProgramCandidatesApi.remove(id),
    onSuccess: () => {
      setDeleting(null);
      qc.invalidateQueries({ queryKey: ['program-candidates'] });
    },
  });

  return (
    <>
      <PageHeader title="Program Candidate Registration" description="Register students for competition items — Islamic Song, Thilava, Thasfiya, Group Song, and more." />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Academic Year">
          <Select value={academicYearId} onChange={(e) => setAcademicYearId(e.target.value)}>
            {years.map((y: any) => (
              <option key={y.id} value={y.id}>
                {y.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Class">
          <Select
            value={classId}
            onChange={(e) => {
              setClassId(e.target.value);
              setSectionId('');
              setStudentId('');
            }}
          >
            <option value="">Select class</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Section">
          <Select
            value={sectionId}
            onChange={(e) => {
              setSectionId(e.target.value);
              setStudentId('');
            }}
            disabled={!classId}
          >
            <option value="">All sections</option>
            {sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Student">
          <Select value={studentId} onChange={(e) => setStudentId(e.target.value)} disabled={!classId}>
            <option value="">Select student…</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name ?? '(no linked user)'} {s.rollNumber ? `— Roll ${s.rollNumber}` : ''} ({s.admissionNo})
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {!studentId ? (
        <div className="card p-8 text-center text-slate-400">Select a student to register for program items.</div>
      ) : (
        <div className="card p-4">
          {existingCandidate && existingCandidate.items.length > 0 && (
            <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Already registered</div>
              <div className="flex flex-wrap gap-2">
                {existingCandidate.items.map((i) => (
                  <Badge key={i.id} tone={TYPE_TONE[i.programItemType] ?? 'slate'}>
                    {i.programItemName} ({TYPE_LABEL[i.programItemType]}
                    {i.isGroupLeader ? ' · Leader' : i.groupLeaderId ? ' · Member' : ''})
                  </Badge>
                ))}
              </div>
              {existingCandidate.chestNumber != null && (
                <div className="mt-2 text-xs text-slate-500">Chest number: {existingCandidate.chestNumber}</div>
              )}
            </div>
          )}

          <div className="mb-2 flex flex-wrap gap-3 text-xs text-slate-500">
            <span>Quran: {totalQuota.quran}/1</span>
            <span>Single: {totalQuota.single}/2</span>
            <span>Group/Team: {totalQuota.group}/1</span>
          </div>

          {eligibleItems.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-400">No program items match this student's grade.</div>
          ) : (
            <div className="space-y-2">
              {eligibleItems.map((item) => {
                const already = existingItemIds.has(item.id);
                const genderBlocked = item.id === GROUP_SONG_ITEM_ID && groupSongGenderMismatch;
                return (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between rounded-md border px-3 py-2 ${already ? 'border-slate-100 bg-slate-50' : 'border-slate-200'}`}
                  >
                    <Checkbox
                      label={`${item.name}${item.code ? ` (${item.code})` : ''}`}
                      checked={already || !!checked[item.id]}
                      disabled={already || genderBlocked || (!checked[item.id] && isDisabled(item)) || !canWrite}
                      onChange={(e) => setChecked((p) => ({ ...p, [item.id]: e.target.checked }))}
                    />
                    <div className="flex items-center gap-2">
                      {item.quranProgram && <Badge tone="amber">Quran</Badge>}
                      <Badge tone={TYPE_TONE[item.type] ?? 'slate'}>{TYPE_LABEL[item.type]}</Badge>
                      {already && <Badge tone="slate">Registered</Badge>}
                      {genderBlocked && (
                        <span className="text-xs text-red-600">
                          Team is all-{groupSongTeamGender} — must be all-boys or all-girls
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {canWrite && (
            <div className="mt-4 flex items-center justify-end gap-3">
              {register.isSuccess && <span className="text-sm text-green-700">✓ Registered.</span>}
              {registerError && <span className="text-sm text-red-600">{registerError}</span>}
              <button className="btn-primary" onClick={() => register.mutate()} disabled={register.isPending || !checkedItems.length}>
                <Save className="mr-1.5 h-4 w-4" />
                {register.isPending ? 'Registering…' : 'Register selected items'}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mt-6">
        <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <Users className="h-4 w-4" /> All registrations ({candidates.length})
        </h3>
        <DataTable
          columns={registrationColumns}
          result={registrationsResult}
          state={registrationsTable}
          emptyMessage="No registrations yet."
          getRowId={(row) => row.id}
        />
      </div>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        title="Remove registration?"
        message={`This removes "${deleting?.studentName}"'s entire program registration (all items). This cannot be undone.`}
        confirmText="Remove"
        destructive
        loading={remove.isPending}
      />
    </>
  );
}
